/**
 * Centralized Social Media Links Configuration
 * 
 * This file contains all social media platform links for Octalchip.
 * Update this file to add, remove, or modify social media links across the entire website.
 */

import { 
  FaDiscord, 
  FaYoutube, 
  FaPinterest, 
  FaReddit, 
  FaWhatsapp, 
  FaTelegram,
  FaMedium 
} from 'react-icons/fa';
import { FaThreads } from 'react-icons/fa6';
import { SiBluesky } from 'react-icons/si';

/**
 * Social media links configuration
 * Each entry contains:
 * - name: Display name of the platform
 * - icon: Icon name for Lucide React (if available) or null
 * - url: Full URL to the social media profile
 * - reactIcon: React icon component from react-icons (if Lucide doesn't have it)
 * - iconLibrary: Library name for react-icons ('fa', 'fa6', 'si', etc.)
 */
export const socialLinks = [
  { 
    name: 'Facebook', 
    icon: 'Facebook', 
    url: 'https://www.facebook.com/octalchip',
    reactIcon: null,
    iconLibrary: null
  },
  { 
    name: 'Instagram', 
    icon: 'Instagram', 
    url: 'https://www.instagram.com/octalchiptech/',
    reactIcon: null,
    iconLibrary: null
  },
  { 
    name: 'LinkedIn', 
    icon: 'Linkedin', 
    url: 'https://www.linkedin.com/company/octalchip',
    reactIcon: null,
    iconLibrary: null
  },
  { 
    name: 'Twitter', 
    icon: 'Twitter', 
    url: 'https://x.com/octalchip',
    reactIcon: null,
    iconLibrary: null
  },
  { 
    name: 'Discord', 
    icon: 'Discord', 
    url: 'https://discord.gg/sNbNevBZ',
    reactIcon: FaDiscord,
    iconLibrary: 'fa'
  },
  { 
    name: 'YouTube', 
    icon: 'Youtube', 
    url: 'https://www.youtube.com/@octalchip',
    reactIcon: null,
    iconLibrary: null
  },
  { 
    name: 'Pinterest', 
    icon: null, 
    url: 'https://www.pinterest.com/octalchip/',
    reactIcon: FaPinterest,
    iconLibrary: 'fa'
  },
  { 
    name: 'Bluesky', 
    icon: null, 
    url: 'https://bsky.app/profile/octalchip.bsky.social',
    reactIcon: SiBluesky,
    iconLibrary: 'si'
  },
  { 
    name: 'Reddit', 
    icon: null, 
    url: 'https://www.reddit.com/user/octalchip',
    reactIcon: FaReddit,
    iconLibrary: 'fa'
  },
  { 
    name: 'WhatsApp', 
    icon: 'MessageCircle', 
    url: 'https://wa.me/917574082582',
    reactIcon: FaWhatsapp,
    iconLibrary: 'fa'
  },
  { 
    name: 'Telegram', 
    icon: null, 
    url: 'https://t.me/octalchip',
    reactIcon: FaTelegram,
    iconLibrary: 'fa'
  },
  { 
    name: 'Threads', 
    icon: 'Instagram', // Fallback to Instagram icon if Threads icon not available
    url: 'https://www.threads.com/@octalchiptech',
    reactIcon: FaThreads,
    iconLibrary: 'fa6'
  },
  { 
    name: 'Medium', 
    icon: null, 
    url: 'https://medium.com/@octalchip',
    reactIcon: FaMedium,
    iconLibrary: 'fa'
  },
  { 
    name: 'Email', 
    icon: 'Mail', 
    url: 'mailto:business@octalchip.com',
    reactIcon: null,
    iconLibrary: null
  }
];

/**
 * Get social links for structured data (JSON-LD)
 * Returns only URLs, excluding email and WhatsApp
 */
export const getSocialLinksForStructuredData = () => {
  return socialLinks
    .filter(link => 
      !link.url.startsWith('mailto:') && 
      !link.url.startsWith('https://wa.me/')
    )
    .map(link => link.url);
};

/**
 * Get React icon component for a social link
 */
export const getSocialIcon = (socialLink) => {
  if (socialLink.reactIcon) {
    return socialLink.reactIcon;
  }
  return null;
};

/**
 * Check if a social link uses a React icon instead of Lucide
 */
export const usesReactIcon = (socialLink) => {
  return socialLink.reactIcon !== null;
};

